import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Platform,
  StyleSheet,
  TextInput,
  type TextInputProps,
} from "react-native";

import type { UiTheme } from "../theme";
import { stateAria } from "../a11y";

// THE shared text input. First generic primitive in @heraion/ui, born to
// end the 40-file TextInput silo (next/consolidation.md) — one box, one fix,
// one place to change. Encodes the real Android-inflation fix ONCE:
// `minHeight` (not a hard height, so the box grows under font-scale instead of
// clipping — the myopic-not-low-vision rule) + `paddingVertical: 0` on the
// single-line box, which otherwise gains a large native vertical pad on Android
// and looks giant (Antenore 2026-07-02). Multiline legitimately needs vertical
// padding, so that mode restores it and pins text to the top.
//
// It is a LEAF (returns the bare TextInput) so it slots into the ~40 diverse
// layouts as a drop-in — the caller owns width/flex/margins via `style` (merged
// last). a11y CONTRACT for `invalid`: on web it sets `aria-invalid`; React
// Native has no equivalent invalid a11y state, so the field alone would be a
// red border and nothing else. `invalid` must therefore ALWAYS be paired with a
// caller-rendered, visible error line beside the field — that is the non-colour
// cue (the reduced-vision floor). The primitive cannot enforce it; the caller
// owns it. Numeric entry folds in via `numeric` (retires the separately-planned
// <NumericField>): decimal-pad + tabular-nums + centre.

/** The compact single-line box height (the default; `touchFloor` lifts it to 44). */
export const TEXT_FIELD_BOX_H = 36;

/** The box's border: a grown web box adds it twice to its text's height. */
const BORDER_W = 1;

/** `touchFloor`: the 44pt tap floor, single-line or multiline. */
const TOUCH_FLOOR_H = 44;

// lib is ES2022 (no DOM): the few web members a growing box reads, by hand.
type GrowNode = { style: { height: string }; scrollHeight: number; clientWidth: number };
type Watcher = { observe(n: GrowNode, o?: { attributes: boolean; attributeFilter: string[] }): void; disconnect(): void };
type FontSet = { addEventListener(t: string, f: () => void): void; removeEventListener(t: string, f: () => void): void };
const web = globalThis as unknown as {
  ResizeObserver?: new (f: () => void) => Watcher;
  MutationObserver?: new (f: () => void) => Watcher;
  getComputedStyle?: (n: GrowNode) => { fontSize: string; lineHeight: string; fontFamily: string };
  document?: { fonts?: FontSet };
};

/** Calls `onChange` when the box's text would wrap anew with the same value:
 *  its width (resize), its type (an inline style), a web font that loads. */
function watchTextBox(node: GrowNode, onChange: () => void): () => void {
  const keyOf = () => {
    const cs = web.getComputedStyle?.(node);
    return `${node.clientWidth}|${cs?.fontSize}|${cs?.lineHeight}|${cs?.fontFamily}`;
  };
  let key = keyOf();
  // Its own height writes change none of these: no loop.
  const check = () => {
    const next = keyOf();
    if (next === key) return;
    key = next;
    onChange();
  };
  const ro = web.ResizeObserver ? new web.ResizeObserver(check) : null;
  ro?.observe(node);
  const mo = web.MutationObserver ? new web.MutationObserver(check) : null;
  mo?.observe(node, { attributes: true, attributeFilter: ["style", "class"] });
  const fonts = web.document?.fonts;
  fonts?.addEventListener("loadingdone", onChange);
  return () => {
    ro?.disconnect();
    mo?.disconnect();
    fonts?.removeEventListener("loadingdone", onChange);
  };
}

export type TextFieldProps = Omit<TextInputProps, "style"> & {
  theme: UiTheme;
  /** Text alignment. Defaults to `center` when `numeric`, else `left`. */
  align?: "left" | "center" | "right";
  /** Growable multi-line box (notes, feedback). Restores vertical padding. */
  multiline?: boolean;
  /** Multiline min height, in text rows (default 2). Ignored single-line. */
  minRows?: number;
  /** Multiline cap, in text rows: the box grows with its text up to it, then
   *  scrolls inside. Absent: no cap. Ignored single-line. */
  maxRows?: number;
  /** Error state — pair with a caller-rendered error line (never colour-alone). */
  invalid?: boolean;
  /** Decimal numeric entry: decimal-pad keyboard + tabular figures + centre. */
  numeric?: boolean;
  /** The box at the 44pt tap floor on every platform, for a field that IS
   *  the tap target (no row around it to borrow height from); multiline too,
   *  as its min height. Default off: the 36 box stays everywhere else. */
  touchFloor?: boolean;
  /** Layout-only override (width/flex/margins). Merged last; box internals stay. */
  style?: TextInputProps["style"];
};

export const TextField = forwardRef<TextInput, TextFieldProps>(function TextField(
  {
    theme,
    align,
    multiline = false,
    minRows = 2,
    maxRows,
    invalid = false,
    numeric = false,
    touchFloor = false,
    style,
    placeholderTextColor,
    keyboardType,
    ...rest
  },
  ref,
) {
  const styles = makeStyles(theme);
  const effectiveAlign = align ?? (numeric ? "center" : "left");
  const row = rowHeight(theme);
  const minH = Math.max(touchFloor ? TOUCH_FLOOR_H : 0, row * minRows + theme.spacing.s3);
  const maxH = maxRows === undefined ? undefined : Math.max(minH, row * maxRows + theme.spacing.s3);
  // A native multiline box grows and shrinks by itself. RNW's textarea does
  // not: at each value, and when the same text wraps anew (watchTextBox), its
  // height drops to 0, so scrollHeight is the text's own (padding included),
  // then takes it plus the border, between min and max. An uncontrolled field
  // with `maxRows` does not grow as it is typed in.
  const webGrow = multiline && maxH !== undefined && Platform.OS === "web";
  const [webH, setWebH] = useState<number | null>(null);
  // The caller's ref gets the same node it always did; this one reads it too.
  const inner = useRef<TextInput | null>(null);
  const setRef = useCallback(
    (el: TextInput | null) => {
      inner.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );
  // On the web the host is the DOM textarea.
  const fit = useCallback(() => {
    const node = inner.current as unknown as GrowNode | null;
    if (!node || maxH === undefined) return;
    node.style.height = "0px";
    const h = Math.min(maxH, Math.max(minH, node.scrollHeight + 2 * BORDER_W));
    node.style.height = `${h}px`;
    setWebH(h);
  }, [minH, maxH]);
  useLayoutEffect(() => {
    if (webGrow) fit();
  }, [webGrow, rest.value, fit]);
  useEffect(() => {
    const node = inner.current as unknown as GrowNode | null;
    if (!webGrow || !node) return undefined;
    // Once more after the paint: a style set since the layout pass is read.
    fit();
    return watchTextBox(node, fit);
  }, [webGrow, fit]);

  return (
    <TextInput
      ref={setRef}
      multiline={multiline}
      keyboardType={keyboardType ?? (numeric ? "decimal-pad" : undefined)}
      placeholderTextColor={placeholderTextColor ?? theme.palette.ink3}
      accessibilityState={{ disabled: rest.editable === false }}
      {...stateAria({ disabled: rest.editable === false })}
      // Non-visual invalid cue so `invalid` is never colour-alone (the red
      // border pairs with a caller-rendered error line for sighted users).
      {...(invalid && Platform.OS === "web" ? { "aria-invalid": true } : {})}
      {...(webGrow ? { rows: 1 } : {})}
      style={[
        styles.base,
        multiline
          ? [
              styles.multiline,
              { minHeight: minH },
              maxH !== undefined && { maxHeight: maxH },
              webGrow && webH !== null && { height: webH },
            ]
          : [styles.singleLine, touchFloor && styles.touchFloor],
        { textAlign: effectiveAlign },
        numeric && styles.numeric,
        invalid && styles.invalid,
        style,
      ]}
      {...rest}
    />
  );
});

// One text row in px at the base size — token-derived, so multiline heights
// track the type scale instead of a magic number.
// ⭐ EXPORTED because a caller that caps this box's height has to count rows in
// the SAME unit the box grows in. PrescribeHeaderFields re-derived this formula
// verbatim first (review finding); two copies of one arithmetic diverge the day
// the type scale moves, and the symptom would be a cap that no longer lands on
// a whole line.
export function rowHeight(theme: UiTheme): number {
  return Math.round(theme.typography.sizes.base * theme.typography.leading.base);
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    base: {
      paddingHorizontal: s.s3,
      borderRadius: r.r3,
      borderWidth: BORDER_W,
      borderColor: p.border,
      backgroundColor: p.surface,
      color: p.ink,
      fontSize: t.sizes.base,
    },
    // minHeight (not height): the box grows under font-scale, never clips.
    // paddingVertical 0: kills Android's native vertical inflation.
    singleLine: { minHeight: TEXT_FIELD_BOX_H, paddingVertical: 0 },
    touchFloor: { minHeight: TOUCH_FLOOR_H },
    multiline: { paddingVertical: s.s2, textAlignVertical: "top" },
    numeric: { fontVariant: ["tabular-nums"] },
    invalid: { borderColor: p.danger },
  });
}
